import { Controller } from "@hotwired/stimulus";

// Máscara internacional dos campos de telefone e WhatsApp do Super Guia, com a mesma regra de
// Guia::PhoneFormat: "+1 404 555 1234", "+55 11 98765 4321", "+55 11 3456 7890". Sem "+", o
// código do país sai do estado escolhido no formulário. Outros países ficam como "+" e dígitos.
//
// O cursor é reposicionado contando dígitos, não caracteres: a máscara insere espaços e, no
// primeiro dígito, o código do país inteiro.
//
// Só ajuda a digitar: o servidor normaliza de novo (o telefone fica com espaços, o WhatsApp só
// com dígitos), então quem desligar o JavaScript não grava nada fora do padrão.
function group(digits, sizes) {
  const parts = [];
  let index = 0;
  for (const size of sizes) {
    if (index >= digits.length) break;
    parts.push(digits.slice(index, index + size));
    index += size;
  }
  return parts.join(" ");
}

// "+55 55 ..." em que o resto não pode ser um número do DDD 55: número no Brasil não começa com
// 0 nem 1, fixo tem 8 dígitos (começa de 2 a 8) e celular tem 9 (começa com 9).
function repeatedBrazilCode(digits) {
  if (!digits.startsWith("5555")) return false;

  const number = digits.slice(4);
  if (!number) return false;
  if (/^[01]/.test(number)) return true;
  return number.startsWith("9") ? number.length > 9 : number.length > 8;
}

// @return {{ value: string, prefixed: number }} o número formatado e quantos dígitos de código
//   do país a máscara acrescentou sozinha (para o cursor não ficar para trás deles).
export function formatPhone(raw, country) {
  const value = raw.trim();
  let digits = value.replace(/\D/g, "");
  if (!digits) return { value: value.startsWith("+") ? "+" : "", prefixed: 0 };

  let prefixed = 0;
  if (!value.startsWith("+")) {
    if (country === "us") {
      // Número americano nunca começa com 1: um 1 na frente é o código do país.
      if (!digits.startsWith("1")) {
        digits = `1${digits}`;
        prefixed = 1;
      }
    } else if (country === "br") {
      // Sem "+", é número nacional (DDD + número): "55" no começo pode ser o DDD do RS. Só um
      // número longo demais para ser nacional já traz o 55.
      if (!(digits.startsWith("55") && digits.length > 11)) {
        digits = `55${digits}`;
        prefixed = 2;
      }
    } else {
      return { value, prefixed: 0 };
    }
  }

  // Quem digita o código do país sem "+" ("5511 9...") recebe um "+55" da máscara no primeiro
  // dígito e o próprio 55 vira DDD. Assim que o resto deixa de caber num número com DDD 55, a
  // cópia sai — e o cursor recua junto.
  if (repeatedBrazilCode(digits)) {
    digits = digits.slice(2);
    prefixed -= 2;
  } else if (digits.startsWith("11")) {
    // Número americano nunca começa com 1: "+1 1..." é o código digitado de novo.
    digits = digits.slice(1);
    prefixed -= 1;
  }

  if (digits.startsWith("1")) {
    return { value: `+1 ${group(digits.slice(1, 11), [3, 3, 4])}`.trim(), prefixed };
  }
  if (digits.startsWith("55")) {
    const national = digits.slice(2, 13);
    const area = national.slice(0, 2);
    const number = national.slice(2);
    // Celular no Brasil começa com 9 e tem 9 dígitos (5-4); fixo tem 8 (4-4).
    const sizes = number.startsWith("9") ? [5, 4] : [4, 4];
    return { value: `+55 ${[area, group(number, sizes)].filter(Boolean).join(" ")}`.trim(), prefixed };
  }
  if (digits === "5") return { value: "+5", prefixed };

  return { value: `+${digits.slice(0, 15)}`, prefixed };
}

export default class extends Controller {
  static targets = ["input", "state"];
  static values = { states: Object };

  connect() {
    this.inputTargets.forEach((input) => this.#apply(input));
  }

  format(event) {
    this.#apply(event.target, event);
  }

  #apply(input, event = null) {
    const raw = input.value;
    // Com ramal ("ext. 12", "ramal 3") o número fica como a pessoa escreveu.
    if (/(ext|ramal|x)\.?\s*\d/i.test(raw)) return;

    let working = raw;
    const caret = input.selectionStart ?? raw.length;
    let digitsBefore = raw.slice(0, caret).replace(/\D/g, "").length;

    // Apagar um espaço da máscara não muda nenhum dígito, e a máscara o devolveria na hora:
    // apaga o dígito anterior junto, que é o que a pessoa quis.
    const previous = input.dataset.phoneMaskPrevious;
    if (event?.inputType === "deleteContentBackward" && previous &&
        previous.replace(/\D/g, "") === raw.replace(/\D/g, "") && digitsBefore > 0) {
      working = this.#removeDigitAt(raw, digitsBefore - 1);
      digitsBefore -= 1;
    }

    const { value: formatted, prefixed } = formatPhone(working, this.#country());
    input.value = formatted;
    input.dataset.phoneMaskPrevious = formatted;

    if (event && document.activeElement === input) {
      const position = this.#positionAfterDigits(formatted, digitsBefore + prefixed);
      input.setSelectionRange(position, position);
    }
  }

  #country() {
    if (!this.hasStateTarget) return null;
    return this.statesValue[this.stateTarget.value] || null;
  }

  #removeDigitAt(value, digitIndex) {
    let seen = -1;
    for (let i = 0; i < value.length; i += 1) {
      if (/\d/.test(value[i])) {
        seen += 1;
        if (seen === digitIndex) return value.slice(0, i) + value.slice(i + 1);
      }
    }
    return value;
  }

  #positionAfterDigits(value, count) {
    if (count <= 0) return value.startsWith("+") ? 1 : 0;
    let seen = 0;
    for (let i = 0; i < value.length; i += 1) {
      if (/\d/.test(value[i])) {
        seen += 1;
        if (seen === count) return i + 1;
      }
    }
    return value.length;
  }
}
